/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, onConnect } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import * as DateF from 'date-fns'

import { BigNumber as BigNumber_, Order } from '@blade/std-lib'
import { currencyRegistry } from '@root/entities/currency/atoms'
import { CurrencyAddress, CurrencyModel } from '@root/entities/currency/model'
import { Network } from '@root/entities/network/model'
import { Provider } from '@root/entities/provider'
import { reatomPairPriceLatest } from '@root/features/services/pair-price'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { NumberUtil } from '@root/shared'
import { reatomBigNumber } from '@root/shared/lib'

import { PairAddress, PairExtraDetails, PairRef } from '../model'

import { reatomAth } from './reatom-ath'
import { favoriteMetasAtom } from './reatom-favorite-metas'
import { reatomPairAudit } from './reatom-pair-audit'
import { reatomPairCreatorStats } from './reatom-pair-creator-stats'
import { reatomPairExtraDetails } from './reatom-pair-extra-details'
import { reatomPairFees } from './reatom-pair-fees'
import { reatomPairHolders } from './reatom-pair-holders'
import { pairInitTxCreatorFromDto } from './reatom-pair-init-tx-creator'
import { reatomPairPeriodStats } from './reatom-pair-metrics'
import { reatomPairPosition } from './reatom-pair-position'
import { reatomPairPriceChart } from './reatom-pair-price-chart'
import { reatomPairTrades } from './reatom-pair-trades'
import { reatomPairUIModel } from './reatom-pair-ui'

import type { Pair, PairMetrics } from '../model'
import type { IPoolInfoExtended } from '@blade/blockchain-types'
import type { tradeApi } from '@blade/socket-types'
import type { Equivalence } from '@blade/std-lib'
import type { Ctx } from '@reatom/framework'
import type { PremiumMetaModel } from '@root/entities/premium/atoms/reatom-premium-meta'
import type { ServicesApi } from '@root/shared/api'
import type { TokenLogosHost } from '@root/shared/models'

class PairModelClass {}

export const reatomPair = (
  dto: ServicesApi.PairDto,
  imagesHost: TokenLogosHost,
  premiumMeta: PremiumMetaModel,
) => {
  const pairInfoSubscriptionTriggerAtom = atom(undefined)
  //#region Static
  const network = Network.fromDto(dto.network)
  const address = PairAddress(dto.poolAddress)
  const quote = currencyRegistry.getOrCreate(
    CurrencyAddress(dto.quote.address),
    network,
    () => CurrencyModel.fromTokenMeta(dto.quote, imagesHost),
  )
  const base = currencyRegistry.getOrCreate(
    CurrencyAddress(dto.base.address),
    network,
    () => CurrencyModel.fromTokenMeta(dto.base, imagesHost),
  )
  const createdAt = dto.initTimestamp ? new Date(dto.initTimestamp) : undefined
  const provider = Provider.fromDto(dto.provider as Provider.Id, {
    isPumpFunMayhem: dto.extraDetails
      ? PairExtraDetails.isPumpFunMayhem(dto.extraDetails)
      : false,
  })
  //#endregion

  //#region State
  const updatedAtAtom = atom(new Date(dto.updatedAt))
  const infoUpdatedAtAtom = atom(
    dto.infoUpdatedAt ? new Date(dto.infoUpdatedAt) : undefined,
  )
  const metricsUpdatedAtAtom = atom(
    dto.metricsUpdatedAt ? new Date(dto.metricsUpdatedAt) : undefined,
  )
  const chartsUpdatedAtAtom = atom(
    dto.chartsUpdatedAt ? new Date(dto.chartsUpdatedAt) : undefined,
  )
  const fallbackPriceAtom = atom({
    priceNative: BigNumber(dto.priceNative),
    priceUsd: BigNumber(dto.priceUsd),
  })
  const rankAtom = atom(dto.quote.trendingRank)
  const { atom: auditAtom, updateFromDto: updateAuditFromDtoAction } =
    reatomPairAudit(dto.audit)
  const firstBuyersAtom = atom(dto.firstBuyers ?? [])

  const liquidityUsdAtom = reatomBigNumber(dto.liquidityUsd)
  const liquidityNativeAtom = reatomBigNumber(dto.liquidityNative)
  const liquidityCurrencyAtom = reatomBigNumber(dto.liquidityToken)

  const marketCapUsdAtom = reatomBigNumber(dto.marketCapUsd)

  const supplyAtom = reatomBigNumber(dto.quote.supply)

  const initTxCreatorAtom = atom(
    dto.initTxCreator ? pairInitTxCreatorFromDto(dto.initTxCreator) : undefined,
  )
  const initTxSignatureAtom = atom(dto.initTxSignature)
  const baseTokenProgramAtom = atom(dto.base.tokenProgram)
  const quoteTokenProgramAtom = atom(dto.quote.tokenProgram)

  const holdersCountAtom = atom(
    NumberUtil.safeParseFloat(dto.holders.count) ?? 0,
  )
  const top10holdingAtom = reatomBigNumber(dto.holders.top10Holding)

  /**
   * The launch groups, which a cheap card leaves out rather than zeroing.
   *
   * Reading them costs a first-buyers query per pool and then a balance and
   * a totals query for every wallet in it, so a list that draws none of them
   * — the wallet tracker's trades, a feed's ranking pass — asks for none and
   * none travel. Absent is not zero: the model starts empty and an update
   * carrying no group leaves the one it holds alone, or the card behind a
   * list row would blank the badges of the pool open beside it.
   */
  const noGroup = { count: '0', holding: '0' }
  const insidersModel = reatomPairHolders(
    dto.insiders ?? noGroup,
    'insidersAtom',
  )
  const snipersModel = reatomPairHolders(dto.snipers ?? noGroup, 'snipersAtom')
  const bundlersModel = reatomPairHolders(
    dto.bundlers ?? noGroup,
    'bundlersAtom',
  )

  const accountsAtom = atom<Pair.Accounts>({
    buy: dto.accounts?.buy as Pair.AccountsDto | undefined,
    sell: dto.accounts?.sell as Pair.AccountsDto | undefined,
  })
  const athPriceAtom = reatomAth(dto.allTimeHigh.price)
  const athMarketCapAtom = reatomAth(dto.allTimeHigh.marketCap)
  const { updateFromDtoAction: updateFeesFromDtoAction, ...feesModel } =
    reatomPairFees(dto)

  const chartsModel = reatomPairPriceChart(dto)
  const {
    updateFromDto: updateExtraDetailsFromDtoAction,
    ...extraDetailsAtoms
  } = reatomPairExtraDetails(dto)

  const {
    stats,
    updateFromDto: updateStatsFromDtoAction,
    applyTrade: applyTradeToStatsAction,
    allTimeTxCount,
  } = reatomPairPeriodStats(dto)

  const creatorStatsModel = reatomPairCreatorStats(
    dto.creatorStats,
    imagesHost,
    network,
  )

  const tradesModel = reatomPairTrades()
  //#endregion

  //#region Computed
  const priceModelAtom = reatomPairPriceLatest({
    network,
    pairAddress: address,
    mainWebsocketSubscribeStrategy: 'without-subscription',
  })
  const realtimePriceModelAtom = reatomPairPriceLatest({
    network,
    pairAddress: address,
    mainWebsocketSubscribeStrategy: 'with-subscription',
  })
  const priceAtom = atom((ctx) => {
    const price = ctx.spy(priceModelAtom)
    if (price) {
      return {
        priceNative: price.priceNative,
        priceUsd: price.priceUsd,
      }
    }
    return ctx.spy(fallbackPriceAtom)
  })
  const priceRealtimeAtom = atom((ctx) => {
    const price = ctx.spy(realtimePriceModelAtom)
    if (price) {
      return {
        priceNative: price.priceNative,
        priceUsd: price.priceUsd,
      }
    }
    return ctx.spy(fallbackPriceAtom)
  })

  // A list row shows two numbers off the first buyers, and the wallets
  // themselves are half the weight of a card, so the feeds send the count and
  // leave the list empty. A card read on its own still carries both.
  const firstBuyersMetaAtom = atom(dto.firstBuyersMeta)

  const snipersMetaAtom = atom((ctx) => {
    const summary = ctx.spy(firstBuyersMetaAtom)
    if (summary) {
      return summary.total === 0
        ? undefined
        : {
            total: summary.total,
            count: summary.snipers,
            percentage: summary.snipers / summary.total,
          }
    }

    const firstBuyers = ctx.spy(firstBuyersAtom)
    if (firstBuyers.length === 0) {
      return undefined
    }
    const snipers = firstBuyers.filter((b) => b.isSniper)

    return {
      total: firstBuyers.length,
      count: snipers.length,
      percentage: snipers.length / firstBuyers.length,
    }
  })

  const favoriteMetaAtom = atom((ctx) =>
    ctx.spy(favoriteMetasAtom).get(address),
  )
  const favoriteAtom = atom((ctx) => {
    const favoriteMeta = ctx.spy(favoriteMetaAtom)
    return Boolean(favoriteMeta)
  })

  const devSoldAllAtom = atom((ctx) => {
    const txCreator = ctx.spy(initTxCreatorAtom)
    const operation = txCreator?.lastTx?.operation
    if (!operation || operation !== 'sell') {
      return false
    }
    const sellVolume = txCreator.sellVolumeInUsd
    if (!sellVolume || sellVolume.isZero()) {
      return false
    }

    const audit = ctx.spy(auditAtom)
    const auditPercent = audit.checks.ownerTokenBalance.percent
    return auditPercent === 0
  })

  //#endregion

  //#region Actions
  const updateMetricsAction = action(
    (ctx, dto: tradeApi.PoolInfoMetricsBlock) => {
      const updatedAt = new Date(dto.updatedAt)
      const currentUpdatedAt = ctx.get(metricsUpdatedAtAtom)
      if (currentUpdatedAt && !DateF.isAfter(updatedAt, currentUpdatedAt)) {
        return
      }

      metricsUpdatedAtAtom(ctx, updatedAt)

      fallbackPriceAtom(ctx, {
        priceNative: BigNumber(dto.priceNative),
        priceUsd: BigNumber(dto.priceUsd),
      })
      marketCapUsdAtom.set(ctx, dto.marketCapUsd)
      liquidityUsdAtom.set(ctx, dto.liquidityUsd)
      liquidityNativeAtom.set(ctx, dto.liquidityNative)
      liquidityCurrencyAtom.set(ctx, dto.liquidityToken)

      updateFeesFromDtoAction(ctx, dto.fees)
      updateStatsFromDtoAction(ctx, dto.periodStats)
      updateAuditFromDtoAction(ctx, dto.audit)
      if (dto.firstBuyers) {
        firstBuyersAtom(ctx, dto.firstBuyers)
      }

      holdersCountAtom(ctx, NumberUtil.safeParseFloat(dto.holders.count) ?? 0)
      top10holdingAtom.set(ctx, dto.holders.top10Holding)

      if (dto.insiders) {
        insidersModel.updateFromDto(ctx, {
          count: dto.insiders.count,
          holding: dto.insiders.holding,
        })
      }
      if (dto.snipers) {
        snipersModel.updateFromDto(ctx, {
          count: dto.snipers.count,
          holding: dto.snipers.holding,
        })
      }
      if (dto.bundlers) {
        bundlersModel.updateFromDto(ctx, {
          count: dto.bundlers.count,
          holding: dto.bundlers.holding,
        })
      }

      if (dto.initTxCreator) {
        initTxCreatorAtom(ctx, pairInitTxCreatorFromDto(dto.initTxCreator))
      }

      athMarketCapAtom.set(ctx, dto.allTimeHigh.marketCap)
      athPriceAtom.set(ctx, dto.allTimeHigh.price)

      if (dto.creatorStats) {
        creatorStatsModel.updateFromDtoAction(ctx, dto.creatorStats)
      }
    },
  )

  const updateChartsAction = action(
    (ctx, dto: tradeApi.PoolInfoChartsBlock) => {
      const currentUpdateAt = ctx.get(chartsUpdatedAtAtom)
      const updatedAt = new Date(dto.updatedAt)
      if (currentUpdateAt && !DateF.isAfter(updatedAt, currentUpdateAt)) {
        return
      }
      chartsUpdatedAtAtom(ctx, updatedAt)
      chartsModel.updateFromDtoAction(ctx, dto.priceChart)
    },
  )

  const updateInfoStaticAction = action(
    (ctx, dto: tradeApi.PoolInfoStaticBlock) => {
      const currentUpdateAt = ctx.get(infoUpdatedAtAtom)
      const updatedAt = new Date(dto.updatedAt)
      if (currentUpdateAt && !DateF.isAfter(updatedAt, currentUpdateAt)) {
        return
      }
      infoUpdatedAtAtom(ctx, updatedAt)

      if (dto.initTxSignature) {
        initTxSignatureAtom(ctx, dto.initTxSignature)
      }

      if (dto.accounts) {
        accountsAtom(ctx, {
          buy: dto.accounts.buy as Pair.AccountsDto,
          sell: dto.accounts.sell as Pair.AccountsDto,
        })
      }

      quote.updateFromTokenMeta(ctx, dto.quote)
      base.updateFromTokenMeta(ctx, dto.base)
    },
  )

  const updateFromPartialAction = action(
    (ctx, dto: tradeApi.PairPartialUpdateData) => {
      if (dto.metrics) {
        updateMetricsAction(ctx, dto.metrics)
      }
      if (dto.charts) {
        updateChartsAction(ctx, dto.charts)
      }
      if (dto.infoStatic) {
        updateInfoStaticAction(ctx, dto.infoStatic)
      }
    },
  )

  const updateFromDtoAction = action(
    (ctx, dto: ServicesApi.PairDto | IPoolInfoExtended) => {
      const updatedAt = new Date(dto.updatedAt)
      const currentUpdatedAt = ctx.get(updatedAtAtom)

      // Creator stats has their own updatedAt
      if (dto.creatorStats) {
        creatorStatsModel.updateFromDtoAction(ctx, dto.creatorStats)
      }

      if (updatedAt < currentUpdatedAt) {
        return
      }

      quote.updateFromTokenMeta(ctx, dto.quote)
      base.updateFromTokenMeta(ctx, dto.base)
      premiumMeta.updateFromDto(ctx, dto.premiumInfo)

      baseTokenProgramAtom(ctx, dto.base.tokenProgram)
      quoteTokenProgramAtom(ctx, dto.quote.tokenProgram)
      if (dto.accounts) {
        accountsAtom(ctx, {
          buy: dto.accounts.buy as Pair.AccountsDto,
          sell: dto.accounts.sell as Pair.AccountsDto,
        })
      }
      updatedAtAtom(ctx, new Date(dto.updatedAt))
      updateExtraDetailsFromDtoAction(ctx, dto)
      fallbackPriceAtom(ctx, {
        priceNative: BigNumber(dto.priceNative),
        priceUsd: BigNumber(dto.priceUsd),
      })
      rankAtom(ctx, dto.base.trendingRank)
      updateAuditFromDtoAction(ctx, dto.audit)
      if (dto.firstBuyers) {
        firstBuyersAtom(ctx, dto.firstBuyers)
      }
      athPriceAtom.set(ctx, BigNumber(dto.allTimeHigh.price))
      athMarketCapAtom.set(ctx, BigNumber(dto.allTimeHigh.marketCap))
      updateFeesFromDtoAction(ctx, dto.fees)

      liquidityUsdAtom.set(ctx, dto.liquidityUsd)
      liquidityNativeAtom.set(ctx, dto.liquidityNative)
      marketCapUsdAtom.set(ctx, dto.marketCapUsd)
      supplyAtom.set(ctx, dto.quote.supply)

      initTxCreatorAtom(
        ctx,
        dto.initTxCreator
          ? pairInitTxCreatorFromDto(dto.initTxCreator)
          : undefined,
      )

      initTxSignatureAtom(ctx, dto.initTxSignature)

      top10holdingAtom.set(ctx, BigNumber(dto.holders.top10Holding))
      holdersCountAtom(ctx, NumberUtil.safeParseFloat(dto.holders.count) ?? 0)

      if (dto.insiders) {
        insidersModel.updateFromDto(ctx, {
          count: dto.insiders.count,
          holding: dto.insiders.holding,
        })
      }
      if (dto.snipers) {
        snipersModel.updateFromDto(ctx, {
          count: dto.snipers.count,
          holding: dto.snipers.holding,
        })
      }
      if (dto.bundlers) {
        bundlersModel.updateFromDto(ctx, {
          count: dto.bundlers.count,
          holding: dto.bundlers.holding,
        })
      }
      chartsModel.updateFromDtoAction(ctx, dto.priceChart)

      updateStatsFromDtoAction(ctx, dto.periodStats)
    },
  )

  const subscribeOnPairInfoAction = action((ctx) => {
    const { mainWebsocket } = ctx.get(dependenciesAtom)
    mainWebsocket.pairInfoSubscribeAction(ctx, {
      network: Network.toSupportedNetwork(network),
      pairAddress: address,
    })
  })

  const unsubscribeFromPairInfoAction = action((ctx) => {
    const { mainWebsocket } = ctx.get(dependenciesAtom)
    mainWebsocket.pairInfoUnsubscribeAction(ctx, {
      network: Network.toSupportedNetwork(network),
      pairAddress: address,
    })
  })
  onConnect(pairInfoSubscriptionTriggerAtom, (ctx) => {
    subscribeOnPairInfoAction(ctx)

    return () => {
      unsubscribeFromPairInfoAction(ctx)
    }
  })

  /**
   * The pool's trades, for whoever wants the figures to move between rebuilds.
   *
   * `pair-update` carries a card the server rebuilt out of ClickHouse, which
   * it can only do on a poll — a watched pool's card arrives about every three
   * seconds at best. A trade arrives when it happens, and the registry counts
   * it into the windows, so transactions and volume climb with the market
   * instead of stepping once the poll comes round.
   */
  const tradesSubscriptionTriggerAtom = atom(undefined)

  onConnect(tradesSubscriptionTriggerAtom, (ctx) => {
    const { chartWebsocket } = ctx.get(dependenciesAtom)
    chartWebsocket.subscribeNewTxAction(ctx, {
      network: Network.toSupportedNetwork(network),
      pairAddress: address,
    })

    return () => {
      chartWebsocket.unsubscribeNewTxAction(ctx, {
        network: Network.toSupportedNetwork(network),
        pairAddress: address,
      })
    }
  })
  //#endregion
  const migratedPairAtom = atom<PairModel | undefined>(undefined)

  const fields = {
    pairInfoSubscriptionTrigger: pairInfoSubscriptionTriggerAtom,
    tradesSubscriptionTrigger: tradesSubscriptionTriggerAtom,
    address,
    quote,
    base,
    createdAt,
    updatedAt: updatedAtAtom,
    network,
    provider,

    premiumInfo: premiumMeta,

    audit: auditAtom,
    rank: rankAtom,
    snipersMeta: snipersMetaAtom,

    liquidityUsd: liquidityUsdAtom,
    liquidityNative: liquidityNativeAtom,
    marketCapUsd: marketCapUsdAtom,
    supply: supplyAtom,

    initTxCreator: initTxCreatorAtom,
    initTxSignature: initTxSignatureAtom,

    /**
     * Will not subscribe to price updates, but will take if there is a subscription
     */
    price: priceAtom,
    /**
     * Upon spying will subscribe to price updates
     */
    priceRealtime: priceRealtimeAtom,
    updateFromDto: updateFromDtoAction,
    updateFromPartial: updateFromPartialAction,
    updateInsiders: insidersModel.updateHolders,
    applyTradeToStats: applyTradeToStatsAction,
    stats,
    allTimeTxCount,
    holdersCount: holdersCountAtom,
    insiders: insidersModel.atom,
    snipers: snipersModel.atom,
    bundlers: bundlersModel.atom,
    top10holding: top10holdingAtom,
    priceChartMinutes: chartsModel.priceChartMinutes,
    priceChartSeconds: chartsModel.priceChartSeconds,
    favoriteMeta: favoriteMetaAtom,
    favorite: favoriteAtom,
    baseTokenProgram: baseTokenProgramAtom,
    quoteTokenProgram: quoteTokenProgramAtom,
    accounts: accountsAtom,
    athPrice: athPriceAtom,
    athMarketCap: athMarketCapAtom,
    fees: feesModel,
    devSoldAllAtom: devSoldAllAtom,

    ...extraDetailsAtoms,
    ...reatomPairPosition({ network, pairAddress: address }),
    ...tradesModel,

    subscribeOnPairInfo: subscribeOnPairInfoAction,
    unsubscribeFromPairInfo: unsubscribeFromPairInfoAction,
    creatorStats: creatorStatsModel.atom,
  }
  const model = {
    ...fields,
    ui: reatomPairUIModel({ pair: fields }),
    migratedPair: migratedPairAtom,
  }

  // Useful for memory snapshot
  Object.setPrototypeOf(model, PairModelClass.prototype)

  return model
}
export interface PairModel_ extends ReturnType<typeof reatomPair> {}

export interface PairModel extends ReturnType<typeof reatomPair> {}

reatomPair.cmpBySplashPointsAsc = (ctx: Ctx): Order.Order<PairModel> => {
  return (a, b) => {
    const pointsA = ctx.spy
      ? ctx.spy(a.premiumInfo.splashPoints)
      : ctx.get(a.premiumInfo.splashPoints)
    const pointsB = ctx.spy
      ? ctx.spy(b.premiumInfo.splashPoints)
      : ctx.get(b.premiumInfo.splashPoints)

    return Order.Number(pointsA, pointsB)
  }
}
reatomPair.cmpPremiumAsc = (ctx: Ctx): Order.Order<PairModel> => {
  return (a, b) => {
    const premiumA = ctx.spy
      ? ctx.spy(a.premiumInfo.premium)
      : ctx.get(a.premiumInfo.premium)
    const premiumB = ctx.spy
      ? ctx.spy(b.premiumInfo.premium)
      : ctx.get(b.premiumInfo.premium)

    return Order.Boolean(premiumA, premiumB)
  }
}
reatomPair.cmpPremiumDesc = (ctx: Ctx): Order.Order<PairModel> => {
  return Order.flip(reatomPair.cmpPremiumAsc(ctx))
}
reatomPair.cmpBySplashPointsDesc = (ctx: Ctx): Order.Order<PairModel> => {
  return Order.flip(reatomPair.cmpBySplashPointsAsc(ctx))
}
reatomPair.cmpByMarketCapAsc = (ctx: Ctx): Order.Order<PairModel> => {
  return (a, b) => {
    const marketCapA = ctx.spy
      ? ctx.spy(a.marketCapUsd)
      : ctx.get(a.marketCapUsd)
    const marketCapB = ctx.spy
      ? ctx.spy(b.marketCapUsd)
      : ctx.get(b.marketCapUsd)
    return BigNumber_.order(marketCapA, marketCapB)
  }
}
reatomPair.cmpByMarketCapDesc = (ctx: Ctx): Order.Order<PairModel> => {
  return Order.flip(reatomPair.cmpByMarketCapAsc(ctx))
}
reatomPair.cmpByLiquidityAsc = (ctx: Ctx): Order.Order<PairModel> => {
  return (a, b) => {
    const liquidityA = ctx.spy
      ? ctx.spy(a.liquidityUsd)
      : ctx.get(a.liquidityUsd)
    const liquidityB = ctx.spy
      ? ctx.spy(b.liquidityUsd)
      : ctx.get(b.liquidityUsd)
    return BigNumber_.order(liquidityA, liquidityB)
  }
}
reatomPair.cmpByLiquidityDesc = (ctx: Ctx): Order.Order<PairModel> => {
  return Order.flip(reatomPair.cmpByLiquidityAsc(ctx))
}

reatomPair.cmpByTxCountAsc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> => {
    return (a, b) => {
      const txCountA = ctx.spy
        ? ctx.spy(a.stats[period].transactions)
        : ctx.get(a.stats[period].transactions)
      const txCountB = ctx.spy
        ? ctx.spy(b.stats[period].transactions)
        : ctx.get(b.stats[period].transactions)
      return Order.Number(txCountA, txCountB)
    }
  }
reatomPair.cmpByTxCountDesc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> => {
    return Order.flip(reatomPair.cmpByTxCountAsc(period)(ctx))
  }

reatomPair.cmpByVolumeUsdAsc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> => {
    return (a, b) => {
      const volumeA = ctx.spy
        ? ctx.spy(a.stats[period].volumeUsd)
        : ctx.get(a.stats[period].volumeUsd)
      const volumeB = ctx.spy
        ? ctx.spy(b.stats[period].volumeUsd)
        : ctx.get(b.stats[period].volumeUsd)
      return BigNumber_.order(volumeA, volumeB)
    }
  }
reatomPair.cmpByVolumeUsdDesc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> => {
    return Order.flip(reatomPair.cmpByVolumeUsdAsc(period)(ctx))
  }

reatomPair.cmpByNetVolumeUsdAsc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> => {
    return (a, b) => {
      const netVolumeA = ctx.spy
        ? ctx.spy(a.stats[period].netVolumeInUsd)
        : ctx.get(a.stats[period].netVolumeInUsd)
      const netVolumeB = ctx.spy
        ? ctx.spy(b.stats[period].netVolumeInUsd)
        : ctx.get(b.stats[period].netVolumeInUsd)
      return BigNumber_.order(netVolumeA, netVolumeB)
    }
  }
reatomPair.cmpByNetVolumeUsdDesc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> => {
    return Order.flip(reatomPair.cmpByNetVolumeUsdAsc(period)(ctx))
  }

reatomPair.cmpByHoldersChangePercentsAsc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> => {
    return (a, b) => {
      const holdersChangeA = ctx.spy
        ? ctx.spy(a.stats[period].holdersChange)
        : ctx.get(a.stats[period].holdersChange)
      const holdersChangeB = ctx.spy
        ? ctx.spy(b.stats[period].holdersChange)
        : ctx.get(b.stats[period].holdersChange)
      return BigNumber_.order(holdersChangeA, holdersChangeB)
    }
  }
reatomPair.cmpByHoldersChangePercentsDesc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> => {
    return Order.flip(reatomPair.cmpByHoldersChangePercentsAsc(period)(ctx))
  }

reatomPair.cmpByPriceChangePercentsAsc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> => {
    return (a, b) => {
      const priceChangeA = ctx.spy
        ? ctx.spy(a.stats[period].priceChange)
        : ctx.get(a.stats[period].priceChange)
      const priceChangeB = ctx.spy
        ? ctx.spy(b.stats[period].priceChange)
        : ctx.get(b.stats[period].priceChange)
      return BigNumber_.order(priceChangeA, priceChangeB)
    }
  }
reatomPair.cmpByPriceChangePercentsDesc =
  (period: PairMetrics.MetricsSpan) =>
  (ctx: Ctx): Order.Order<PairModel> =>
    Order.flip(reatomPair.cmpByPriceChangePercentsAsc(period)(ctx))

reatomPair.cmpTrending = (ctx: Ctx): Order.Order<PairModel> => {
  return Order.combine(
    reatomPair.cmpPremiumDesc(ctx),
    reatomPair.cmpBySplashPointsDesc(ctx),
  )
}
const cmpByCreatedAtAsc: Order.Order<PairModel> = (a, b) => {
  return Order.nullable<Date | undefined>(Order.Date)(a.createdAt, b.createdAt)
}
reatomPair.cmpByCreatedAtAsc = cmpByCreatedAtAsc
reatomPair.cmpByCreatedAtDesc = Order.flip(cmpByCreatedAtAsc)

reatomPair.cmpByHoldersCountAsc = (ctx: Ctx): Order.Order<PairModel> => {
  return (a, b) => {
    const holdersCountA = ctx.spy
      ? ctx.spy(a.holdersCount)
      : ctx.get(a.holdersCount)
    const holdersCountB = ctx.spy
      ? ctx.spy(b.holdersCount)
      : ctx.get(b.holdersCount)

    return Order.Number(holdersCountA, holdersCountB)
  }
}
reatomPair.cmpByHoldersCountDesc = (ctx: Ctx): Order.Order<PairModel> => {
  return Order.flip(reatomPair.cmpByHoldersCountAsc(ctx))
}
const eqByAddress: Equivalence.Equivalence<PairModel> = (self, other) => {
  return PairRef.eq(self, other)
}
reatomPair.eqByAddress = eqByAddress
